import React from "react";
import { Box, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import moment from "moment";
import { friendlyError, partErrorContext, stageLabel } from "../utils/friendlyError";
import {
  insurerColor,
  isDonePart,
  partName,
  partResultLines,
  partStatusMeta,
  partsSummary,
  shortLabel,
  realParts,
} from "../utils/policyParts";

const NAVY = "#1E3A5F";
const stamp = (d) => (d ? moment(d).format("DD MMM YYYY, hh:mm A") : "");

/** What the operator should know about a part besides its status. */
const notesFor = (part) => {
  const notes = [];
  if (part.kind === "withPart" && part.mirrors) {
    notes.push(`Bought together with ${shortLabel(part.mirrors)}`);
  }
  if (part.kind === "brisk") notes.push("Brisk CPA certificate");
  if (part.waitingFor) notes.push(`Waiting for ${shortLabel(part.waitingFor)} to finish`);
  if (part.blockedBy) notes.push(`${shortLabel(part.blockedBy)} failed — retry it and this starts by itself`);
  if (part.routingChanged) {
    notes.push(
      `Insurer changed after purchase: ${part.routingChanged.from || "—"} → ${part.routingChanged.to || "—"} (the bought policy is unchanged)`
    );
  }
  return notes;
};

/** The picture in a part's circle, by the tone of its status. */
const TONE_ICON = {
  success: CheckRoundedIcon,
  warn: PriorityHighRoundedIcon,
  danger: PriorityHighRoundedIcon,
  stopped: PriorityHighRoundedIcon,
  info: AutorenewRoundedIcon,
  muted: RemoveRoundedIcon,
};

/** One fact of a part: its label, then its value, on one line that wraps as a whole. */
const Fact = ({ label, mono = false, children }) => (
  <Typography component="div" sx={{ fontSize: 12, lineHeight: "18px", color: "#667085", minWidth: 0 }}>
    {label}{" "}
    <Box component="span" sx={{ color: "#101828", fontWeight: 600, overflowWrap: "anywhere", fontFamily: mono ? "monospace" : undefined }}>
      {children}
    </Box>
  </Typography>
);

/**
 * One part as a step of a timeline (the owner, 2026-10-30, instead of a boxed tile): a circle in the status's colour, a
 * line down to the next part, then its name, status and facts. `term` / `renderValue` keep its text findable by the page
 * search.
 */
const PartStep = ({ part, term, renderValue }) => {
  const meta = partStatusMeta(part);
  const insurer = insurerColor(part.company).main;
  const done = isDonePart(part);
  const results = partResultLines(part);
  const notes = notesFor(part);

  const facts = [
    part.startedAt && ["Started", stamp(part.startedAt)],
    done && part.completedAt && ["Completed", stamp(part.completedAt)],
    !done && part.status === "pending" && part.nextRetryAt && new Date(part.nextRetryAt) > new Date() && ["Next retry", stamp(part.nextRetryAt)],
    !done && part.stage && ["Step", stageLabel(part.stage)],
  ].filter(Boolean);

  // the error in plain words (utils/friendlyError.js)
  const friendly = part.lastError ? friendlyError(part.lastError, partErrorContext(part)) : null;

  // Same rule as DetailField: matched on anything the step shows.
  const haystack = [
    partName(part),
    meta.label,
    meta.longLabel,
    part.lastError,
    friendly && friendly.title,
    friendly && friendly.message,
    ...facts.flat(),
    ...results.flat(),
    part.result?.warning,
    ...notes,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  const isMatch = !!term && haystack.includes(term.toLowerCase());

  const StatusIcon = TONE_ICON[meta.tone] || ScheduleRoundedIcon;

  return (
    <Box
      data-search-match={isMatch ? "true" : undefined}
      sx={{
        position: "relative",
        display: "grid",
        gridTemplateColumns: "26px minmax(0, 1fr)",
        columnGap: 1,
        p: 0.75,
        borderRadius: "8px",
        backgroundColor: isMatch ? "#FFFCF5" : "transparent",
        boxShadow: isMatch ? "inset 0 0 0 1px #FEC84B" : "none",
        scrollMarginTop: "96px", // clears the sticky header when scrolled to
        // the line down to the next part
        "&:not(:last-of-type)::after": {
          content: '""',
          position: "absolute",
          left: "18px",
          top: "36px",
          bottom: "-4px",
          width: "2px",
          backgroundColor: "#E4E7EC",
        },
      }}
    >
      <Box sx={{ width: 26, height: 26, borderRadius: "50%", backgroundColor: meta.color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <StatusIcon sx={{ fontSize: 16 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", columnGap: 1, minHeight: 26 }}>
          <Typography component="div" sx={{ fontWeight: 700, fontSize: 14, lineHeight: "20px", color: NAVY }}>
            {renderValue(part.label || shortLabel(part.key))}
            {part.company && (
              <Box component="span" sx={{ ml: 0.75, fontSize: 12.5, fontWeight: 600, color: insurer }}>
                {renderValue(part.company)}
              </Box>
            )}
          </Typography>
          <Typography component="div" sx={{ fontSize: 12, fontWeight: 700, lineHeight: "20px", color: meta.color }}>
            {renderValue(meta.label)}
          </Typography>
        </Box>

        {facts.length + results.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 2 }}>
            {facts.map(([label, value]) => (
              <Fact key={label} label={label}>
                {renderValue(value)}
              </Fact>
            ))}
            {results.map(([label, value]) => (
              <Fact key={label} label={label} mono>
                {renderValue(value)}
              </Fact>
            ))}
          </Box>
        )}

        {!done && part.lastError && (
          <Box
            sx={{
              mt: 0.75,
              p: 1,
              borderRadius: "6px",
              border: "1px solid #f3d6d6",
              backgroundColor: "#fdf7f7",
            }}
          >
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#9b1c1c" }}>{renderValue(friendly.title)}</Typography>
            {friendly.message && (
              <Typography sx={{ fontSize: 12, color: "#b3261e", overflowWrap: "anywhere", mt: 0.25 }}>
                {renderValue(friendly.message)}
              </Typography>
            )}
            {friendly.action && (
              <Typography sx={{ fontSize: 11.5, color: "#475467", mt: 0.5 }}>
                <strong>What to do</strong> - {friendly.action}
              </Typography>
            )}
          </Box>
        )}

        {part.result?.warning && (
          <Typography sx={{ fontSize: 11.5, color: "#B54708", mt: 0.5, overflowWrap: "anywhere" }}>
            {renderValue(part.result.warning)}
          </Typography>
        )}

        {notes.map((note) => (
          <Typography key={note} sx={{ fontSize: 11.5, color: "#667085", fontStyle: "italic", mt: 0.25 }}>
            {renderValue(note)}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

/**
 * The View page's "Automation status": a segmented progress bar (one segment
 * per part in its status colour) and a timeline with one step per part.
 *
 * @param {Array}    parts        the policy's parts
 * @param {string}   statusLabel  "OD pending, TP completed, PA waiting for OD"
 * @param {string}   term         the in-page search term
 * @param {Function} renderValue  wraps a string so matches are highlighted
 * @param {boolean}  bare         opt-in: no frame of its own (it already sits in a section card - the View page)
 */
const AutomationStatusCard = ({
  parts,
  statusLabel = "",
  term = "",
  renderValue = (v) => v,
  bare = false,
}) => {
  const list = realParts(parts).filter((p) => p.status !== "cancelled");
  if (!list.length) return null;
  const summary = partsSummary(list);

  return (
    <Box
      sx={
        bare
          ? {}
          : {
              p: 1.5,
              borderRadius: "10px",
              border: "1px solid #EAECF0",
              backgroundColor: "#FCFCFD",
            }
      }
    >
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", columnGap: 1, mb: 0.5, px: bare ? 0.75 : 0 }}>
        {/* bare: the section around it already has the heading */}
        {!bare && <Typography sx={{ fontWeight: 700, fontSize: 14, color: NAVY }}>Automation status</Typography>}
        {list.length > 1 && statusLabel && (
          <Typography sx={{ fontSize: 12, color: "#667085" }}>{renderValue(statusLabel)}</Typography>
        )}
        {summary.text && (
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#667085" }}>{summary.text}</Typography>
        )}
      </Box>

      {list.length > 1 && (
        <Box sx={{ display: "flex", gap: "2px", mb: 0.75, px: bare ? 0.75 : 0 }} aria-hidden="true">
          {list.map((p) => (
            <Box
              key={p.key}
              title={`${partName(p)} — ${partStatusMeta(p).label}`}
              sx={{ flex: 1, height: 4, borderRadius: "2px", backgroundColor: partStatusMeta(p).color }}
            />
          ))}
        </Box>
      )}

      {list.map((part) => (
        <PartStep key={part.key} part={part} term={term} renderValue={renderValue} />
      ))}
    </Box>
  );
};

export default AutomationStatusCard;
